import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { initMetaPixel, trackMetaEvent } from "@/lib/meta-pixel";
import { BrandLogo } from "@/components/BrandLogo";
import {
  isReviewMode,
  resolveOutcome,
  resolveStormVersion,
  STORM_BYLINE,
  STORM_DISCLAIMER,
  STORM_OFFER,
} from "./storm-landing";
import { StormOfferCard } from "./StormOfferCard";
import { StormReviewBar } from "./StormReviewBar";
import { Testimonials } from "@/components/Testimonials";

/**
 * Ten near-identical pages indexed by Google would compete with each other and
 * with the real site, so the page asks not to be indexed. Added on mount and
 * removed on unmount, because this is a SPA and the next page is not an ad page.
 * public/_headers sends the same instruction as a header for crawlers that do
 * not run scripts.
 */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

/**
 * PageView once per visit; nothing at all when no pixel id is configured, or
 * on the review link, so Roger reading drafts never counts as ad traffic.
 */
function useMetaPageView(enabled: boolean) {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !enabled) return;
    sent.current = true;
    if (initMetaPixel()) trackMetaEvent("PageView");
  }, [enabled]);
}

export function StormLandingPage() {
  const { search } = useLocation();
  const version = useMemo(() => resolveStormVersion(search), [search]);
  const review = isReviewMode(search);

  useDocumentMeta({
    title: `${version.hook} — Dividends Roundtable`,
    description: STORM_OFFER.subtitle,
  });
  useNoIndex();
  useMetaPageView(!review);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {review && <StormReviewBar version={version} />}
      <header className="border-b border-border px-4 py-1.5 sm:py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-center gap-2">
          <BrandLogo className="h-9 w-9" />
          <p className="font-heading text-base font-bold sm:text-lg">Dividends Roundtable</p>
        </div>
      </header>

      <main className="flex-1 px-4 pt-4 pb-6 sm:py-12">
        <div className="mx-auto max-w-2xl">
          {/* Roger's face before the question: on a phone a byline under the
              form is never seen, and the person writing is what earns trust. */}
          <div className="mb-3 flex items-center justify-center gap-3">
            <img
              src="/images/roger_profile.jpg"
              alt="Roger Conrad"
              className="h-24 w-24 rounded-full border-2 border-primary/40 object-cover"
            />
            <p className="text-lg leading-snug text-foreground">
              <span className="text-xl font-semibold">Roger Conrad</span>
              <br />
              {STORM_BYLINE}
            </p>
          </div>

          <h1 className="text-balance text-center font-heading text-[1.75rem] font-bold leading-tight sm:text-4xl">
            {version.hook}
          </h1>

          {/* Most ad traffic is on phones, so only the question stands between
              the reader and the form: the button has to show without scrolling.
              The readers follow it. */}
          <StormOfferCard
            landingVariant={version.slug}
            outcome={resolveOutcome(version, review)}
            review={review}
          />
          {/* Right after the form, before the pitch: readers vouching for Roger
              beat Roger explaining himself. The quotes are the readers' words,
              so the copy rules' test lets them name tickers and returns. */}
          <section className="mt-8 text-center" aria-labelledby="storm-readers">
            <h2 id="storm-readers" className="font-heading text-xl font-bold sm:text-2xl">
              Trusted by 1,500+ income investors on Substack
            </h2>
            {/* Counts given by the owner on 2026-10-27: Roger's Substack list,
                and Capitalist Times' paying subscribers across its publications. Recheck before
                raising either. */}
            <p className="mt-1 text-base text-foreground">
              &hellip;plus 1,200 paying subscribers at Capitalist Times, the company Elliot Gue and I
              founded.
            </p>
            <Testimonials />
          </section>

          <p className="mt-8 text-center text-base text-foreground" data-testid="storm-disclaimer">
            {STORM_DISCLAIMER}
          </p>
        </div>
      </main>

      <footer className="border-t border-border px-4 py-4">
        <nav className="mx-auto flex max-w-2xl justify-center gap-4 text-base text-foreground">
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <Link to="/disclaimer" className="hover:underline">Disclaimer</Link>
          <Link to="/terms" className="hover:underline">Terms</Link>
        </nav>
      </footer>
    </div>
  );
}
